/**
 * Круглая неоморфная кнопка с box-shadow
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';

export class NeumorphicCircleButton extends PIXI.Container {
  private bg: PIXI.Sprite;
  private label: PIXI.Text;
  private size: number;

  constructor(text: string, size = 44) {
    super();
    this.size = size;

    const cx = size / 2;
    const texture = this.createTexture();
    this.bg = new PIXI.Sprite(texture);
    this.bg.anchor.set(0.5);
    this.bg.x = cx;
    this.bg.y = cx;
    this.addChild(this.bg);

    this.label = new PIXI.Text(text, new TextStyle({
      fontFamily: '"Jost"',
      fontSize: size * 0.55,
      fill: COLORS.textPanel,
      fontWeight: 'bold',
    }));
    this.label.anchor.set(0.5);
    this.label.x = cx;
    this.label.y = cx - 3;
    this.addChild(this.label);

    this.pivot.set(cx, cx);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new PIXI.Circle(cx, cx, size / 2);
  }

  private createTexture(): PIXI.Texture {
    const s = this.size;
    const r = s / 2;
    const canvas = document.createElement('canvas');
    canvas.width = s;
    canvas.height = s;
    const ctx = canvas.getContext('2d')!;

    ctx.save();
    ctx.beginPath();
    ctx.arc(r, r, r - 1, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    const grad = ctx.createLinearGradient(0, 0, s, s);
    grad.addColorStop(0.1288, '#16181d');
    grad.addColorStop(0.8719, '#181c2a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);

    const darkOuter = ctx.createRadialGradient(r, r, r - 4, r + 6, r + 6, r + 10);
    darkOuter.addColorStop(0, 'transparent');
    darkOuter.addColorStop(0.5, 'rgba(10, 12, 17, 0.25)');
    darkOuter.addColorStop(1, 'rgba(10, 12, 17, 0.5)');
    ctx.fillStyle = darkOuter;
    ctx.fillRect(0, 0, s, s);

    const lightOuter = ctx.createRadialGradient(r - 6, r - 6, 0, r - 8, r - 8, r + 8);
    lightOuter.addColorStop(0, 'rgba(35, 39, 50, 0.45)');
    lightOuter.addColorStop(0.5, 'rgba(35, 39, 50, 0.15)');
    lightOuter.addColorStop(1, 'transparent');
    ctx.fillStyle = lightOuter;
    ctx.fillRect(0, 0, s, s);

    const darkInner = ctx.createRadialGradient(r + 4, r + 4, 0, r + 4, r + 4, r);
    darkInner.addColorStop(0, 'transparent');
    darkInner.addColorStop(0.6, 'transparent');
    darkInner.addColorStop(1, 'rgba(10, 12, 17, 0.5)');
    ctx.fillStyle = darkInner;
    ctx.fillRect(0, 0, s, s);

    const lightInner = ctx.createRadialGradient(r - 5, r - 5, 0, r - 6, r - 6, r);
    lightInner.addColorStop(0, 'rgba(36, 39, 50, 0.45)');
    lightInner.addColorStop(0.5, 'transparent');
    lightInner.addColorStop(1, 'transparent');
    ctx.fillStyle = lightInner;
    ctx.fillRect(0, 0, s, s);

    ctx.restore();

    return PIXI.Texture.from(canvas);
  }

  setEnabled(enabled: boolean): void {
    this.eventMode = enabled ? 'static' : 'auto';
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1 : 0.5;
  }

  get width(): number {
    return this.size;
  }

  get height(): number {
    return this.size;
  }
}
